"use client";

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { 
  LayoutDashboard, Users, FileText, KanbanSquare, Receipt, BarChart3, 
  Settings, Clock, Calendar, Plus, Search 
} from 'lucide-react';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CommandPalette = ({ open, onOpenChange }: CommandPaletteProps) => {
  const navigate = useNavigate();

  const quickNavigationItems = [
    { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, description: 'Go to dashboard' },
    { title: 'Clients', url: '/clients', icon: Users, description: 'Manage clients' },
    { title: 'Projects', url: '/projects', icon: FileText, description: 'View projects' },
    { title: 'Project Tasks', url: '/project-tasks', icon: KanbanSquare, description: 'Manage project tasks' },
    { title: 'Invoices', url: '/invoices', icon: Receipt, description: 'View invoices' },
    { title: 'Analytics', url: '/analytics', icon: BarChart3, description: 'View analytics' },
    { title: 'Time Management', url: '/time-management', icon: Clock, description: 'Track time' },
    { title: 'Scheduled Tasks', url: '/scheduled-tasks', icon: Calendar, description: 'View scheduled tasks' },
    { title: 'Proposals', url: '/proposals', icon: FileText, description: 'Manage proposals' },
    { title: 'Settings', url: '/settings', icon: Settings, description: 'Application settings' },
  ];

  const quickActions = [
    { 
      title: 'New Client', 
      action: () => {
        navigate('/clients');
        // Trigger client creation after navigation
        setTimeout(() => {
          const event = new CustomEvent('trigger-new-client');
          window.dispatchEvent(event);
        }, 100);
      }, 
      icon: Users, 
      description: 'Create a new client' 
    },
    { 
      title: 'New Project', 
      action: () => {
        navigate('/projects');
        setTimeout(() => {
          const event = new CustomEvent('trigger-new-project');
          window.dispatchEvent(event);
        }, 100);
      }, 
      icon: FileText, 
      description: 'Create a new project' 
    },
    { 
      title: 'New Invoice', 
      action: () => {
        navigate('/invoices');
        setTimeout(() => {
          const event = new CustomEvent('trigger-new-invoice');
          window.dispatchEvent(event);
        }, 100);
      }, 
      icon: Receipt, 
      description: 'Create a new invoice' 
    },
    { 
      title: 'New Task', 
      action: () => {
        navigate('/scheduled-tasks');
        setTimeout(() => {
          const event = new CustomEvent('trigger-new-task');
          window.dispatchEvent(event);
        }, 100);
      }, 
      icon: Calendar, 
      description: 'Create a new task' 
    },
    { 
      title: 'New Proposal', 
      action: () => {
        navigate('/proposals');
        setTimeout(() => {
          const event = new CustomEvent('trigger-new-proposal');
          window.dispatchEvent(event);
        }, 100);
      }, 
      icon: FileText, 
      description: 'Create a new proposal' 
    },
  ];

  const handleSelect = (action: () => void) => {
    action();
    onOpenChange(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        
        <CommandGroup heading="Quick Navigation">
          {quickNavigationItems.map((item) => (
            <CommandItem
              key={item.url}
              onSelect={() => handleSelect(() => navigate(item.url))}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.title}</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {item.description}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandGroup heading="Quick Actions">
          {quickActions.map((item) => (
            <CommandItem
              key={item.title}
              onSelect={() => handleSelect(item.action)}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.title}</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {item.description}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;